import {
  ExtractFromSegmentProfiler,
  FallbackProfiler,
  GraphHopperRouter,
  HistoryManager,
  SwisstopoProfiler,
  TrackManager,
  UnsnappedDensifier,
} from "@geoblocks/edittrack";
import { type ParsedGPX, parseGPX } from "@we-gold/gpxjs";
import { Collection } from "ol";
import { doubleClick, singleClick } from "ol/events/condition";
import type Feature from "ol/Feature";
import KML from "ol/format/KML";
import type { Geometry } from "ol/geom";
import type LineString from "ol/geom/LineString.js";
import type Point from "ol/geom/Point.js";
import Snap from "ol/interaction/Snap";
import VectorLayer from "ol/layer/Vector";
import type OLMap from "ol/Map";
import type MapBrowserEvent from "ol/MapBrowserEvent";
import { transform } from "ol/proj";
import type { VectorSourceEvent } from "ol/source/Vector";
import VectorSource from "ol/source/Vector";
import type View from "ol/View";
import type { HikingType, ServerHiking, UnsavedHiking } from "./api-hiking";
import { createHiking, fetchHiking, hikingTypes, readFeatures, updateHiking, writeFeatures } from "./api-hiking";
import { getProfile, type HikingProfile } from "./ElevationProfile";
import { getGpxControlPoints, getGpxCoordinates } from "./gpx";
import { getKmlControlPoints, getKmlCoordinates } from "./kml";
import { TRACK_Z_INDEX } from "./style/common";
import { shadowTrackStyle, trackStyle } from "./style/hiking";
import swissNames from "./swissname";
import { enumerate, indexToLabel, pairwise, toGlobalIndex } from "./utils";
import {
  type Discipline,
  distance,
  type PhysicalDemand,
  walkingAscentDescent,
  walkingPhysicalDemand,
  walkingTime,
} from "./walking";

export type StopOver = {
  uid: string;
  part: number;
  index: number;
  coordinates: number[];
  properties: {
    /**
     * List of swiss names for this location
     */
    swissNames: string[];
    /**
     * The name of the stopover set by the {@link HikingEditor.setStopOverName} function
     */
    customName?: string;
    /**
     * The features of the stopover set by the {@link HikingEditor.setStopOverFeatures} function
     */
    features: unknown[];
    /**
     * Computed walking time duration of the stopover from the previous stopover in minutes
     */
    duration: number;
    /**
     * The duration of the stopover set by the {@link HikingEditor.setStopOverDuration} function
     */
    customDuration?: number;
    /**
     * Computed distance of the stopover from the previous stopover in meters
     */
    distance: number;
    /**
     * The distance of the stopover set by the {@link HikingEditor.setStopOverDistance} function
     */
    customDistance?: number;
    /**
     * Computed ascent of the stopover from the previous stopover in meters
     */
    ascent: number;
    /**
     * Computed descent of the stopover from the previous stopover in meters
     */
    descent: number;
  };
};

export type HikingEditorOptions = {
  /**
   * An OpenLayers map instance
   */
  map: OLMap;
  /**
   * The type of the hiking: {@link HikingEditor.HikingType}
   */
  type: HikingType;
  /**
   * The optional uuid of the hiking to edit
   */
  uuid?: string;
  /**
   * The optional Graphhopper URL
   */
  graphhopperUrl?: string;
};

type StopoverProperties = {
  time: number;
  distance: number;
  ascent: number;
  descent: number;
};

type TrackProperties = {
  totals: {
    time: number;
    distance: number;
    ascent: number;
    descent: number;
    physicalDemand: PhysicalDemand | undefined;
  };
  stopovers: StopoverProperties[];
};

/**
 * # HikingEditor
 *
 * ## Demos
 *  - [Create or edit existing hiking](/demo/editor-base.html)
 *
 * ## Description
 *
 * The HikingEditor class is responsible for editing hiking tracks and stopovers.
 *
 * To construct a new HikingEditor, you need to pass an OpenLayers map instance:
 * ```js
 * const hikingEditor = new sww.HikingEditor({
 *   map: map,
 *   type: "hiking_proposition",
 * });
 * ```
 *
 * This will allow the user to create a new hiking proposition.
 *
 * To edit an existing hiking activity, you need to pass the uuid of the hiking and the type:
 * ```js
 * const hikingEditor = new sww.HikingEditor({
 *   map: map,
 *   type: "hiking_activity",
 *   uuid: "12345678-1234-1234-1234-123456789012",
 * });
 * ```
 * When the hiking is loaded, a `loaded` event is fired. The event detail contains the same properties as the `change` event.
 *
 * The constructor takes an option object {@link HikingEditor.HikingEditorOptions} with the following properties:
 * * `map`: the OpenLayers map instance
 * * `type`: the type of the hiking, one of `hiking`, `hiking_activity` or `hiking_proposition`
 * * `uuid`: the optional uuid of the hiking to edit
 *
 * The `type` property can be changed with the {@link HikingEditor.setType} function:
 * ```js
 * hikingEditor.setType("hiking");
 * ```
 *
 * An other hiking can be loaded with the {@link HikingEditor.loadHiking} function:
 * ```js
 * hikingEditor.loadHiking("12345678-1234-1234-1234-123456789012");
 * ```
 *
 * ## Create or update hiking
 *
 * To save the hiking, call the {@link HikingEditor.save} function, this will create or update the hiking on the server.
 * The geometry can be removed by setting the `coordinates` property to an empty array.
 *
 * ## Deleting
 *
 * To delete the hiking, call the {@link HikingEditor.delete} function, this will delete the hiking from the server.
 *
 * ## Update hiking properties
 *
 * To update the properties of the hiking that are not related to the geometries (for example the title, description,
 * or any other property), call the {@link HikingEditor.updateHikingProperties} function and pass an object with the
 * properties to update:
 * ```js
 * hikingEditor.updateHikingProperties({
 *   title: {"fr": "title fr", "de": "title de", "it": "title it", "en": "title en"},
 *   best_hiking_time: [1, 2],
 * });
 * ```
 * FIXME: what if the the hiking is not saved yet ?
 *
 * ## User interaction
 *
 * * A new control point is added with a single click on the map.
 * * A new control point is added between on a line by dragging the line.
 * * The control points and stopovers can be moved by dragging.
 * * The control points and stopovers can be removed by double clicking on them.
 * * A new stopover is created by clicking on a control point.
 *
 * After each user interaction, a `change` event is fired.
 *
 * ## Edit mode
 *
 * By default, the editor is in `edit` mode. In this mode, the user can add, move and remove control points.
 * To switch to `view` mode, set the `edit` property to `false`:
 * ```js
 * hikingEditor.edit = false;
 * ```
 *
 * ## Snapping mode
 *
 * The lines are automatically snapped to the closest hiking path. To disable snapping, set the
 * `snapping` property to `false`:
 * ```js
 * hikingEditor.snapping = false;
 * ```
 *
 * ## Reverse the track
 * To reverse the track, call the {@link HikingEditor.reverse} function:
 * ```js
 * hikingEditor.reverse();
 * ```
 *
 * The reverse function will trigger a `change` event.
 *
 * ## Undo/Redo
 *
 * The editor can undo or redo the actions to the geometries of the control points.
 * To undo the last action, call the {@link HikingEditor.undo} function:
 * ```js
 * hikingEditor.undo();
 * ```
 * To redo the last action, call the {@link HikingEditor.redo} function:
 * ```js
 * hikingEditor.redo();
 * ```
 *
 * * `canUndo`: true if the user can undo the last action
 * * `canRedo`: true if the user can redo the last action
 *
 * The undo and redo functions will trigger a `change` event.
 *
 * ## Control points
 *
 * The **control points** are the points that are between the segments of the track, they can be added,
 * moved and removed by the user. They are represented by a small circle on the map.
 *
 * ## Stopovers
 * The **stopovers** are a special type of control points that the user chooses to create by clicking
 * on a control point. The first and last control points are always stopovers.
 * They are represented by a big circle on the map.
 *
 * A stopover has a name and and an optional list of features. A duration and a distance from the previous stopover
 * is computed for each stopover.
 *
 * ### Change Event
 *
 * Every time a change is made to the stopovers, a `change` event is fired. The event detail contains the values
 * of the stopovers.
 *
 * To register an event listener for the `change` event, use the `addEventListener` method:
 * ```js
 * hikingEditor.addEventListener("change", (event) => {
 *   for (const stopover of event.detail.stopovers) {
 *     console.log(stopover);
 *   }
 * });
 * ```
 * All the stopovers are returned in the `stopovers` property of the event detail, each stopover has
 * the following properties:
 * - `uid`: the unique id of the stopover. this id is used to identify the stopover in the other methods.
 * - `swissNames`: an array of swiss names for this location
 * - `name`: the name of the stopover, if the name was not set by the {@link HikingEditor.setStopOverName} function, the first swiss name is used.
 * - `features`: an array of features that the user can add to the stopover
 * - `duration`: the walking time duration of the stopover from the previous stopover in minutes. This value can be overwritten by using the {@link HikingEditor.setStopOverDuration} function.
 * - `distance`: the distance of the stopover from the previous stopover in meters. This value can be overwritten by using the {@link HikingEditor.setStopOverDistance} function.
 * - `coordinates`: the coordinates of the stopover in EPSG:4326 projection
 *
 * In addition to the stopovers, the event detail contains the following properties:
 * - `type`: the type of the hiking, one of `hiking`, `hiking_activity` or `hiking_proposition`
 * - `canUndo`: true if the user can undo the last action
 * - `canRedo`: true if the user can redo the last action
 *
 * ### Change a stopover name
 *
 * To change the name of a stopover, use the {@link HikingEditor.setStopOverName} function and pass the uid of the stopover:
 * ```js
 * hikingEditor.setStopOverName(123, "Bümpliz Nord Bahnhof");
 * ```
 *
 * Calling this method will trigger a `change` event.
 *
 * ### Set features to a stopover
 *
 * Every stopover can have a list of features representing a point of interest at this location.
 * A feature is an object with a `type` property and an optional `title` property.
 *
 * To set the features of a stopover, use the {@link HikingEditor.setStopOverFeatures} function and pass the uid of the stopover:
 * ```js
 * hikingEditor.setStopOverFeatures(123, [
 *   { type: "shopping", title: "Food and Stuff" },
 *   { type: "parking" },
 * ]);
 * ```
 * When calling this function, existing features are replaced by the new list.
 *
 * Calling this method will trigger a `change` event.
 *
 * ### Set duration to a stopover
 * To set a custom duration of a stopover in minutes, use the {@link HikingEditor.setStopOverDuration} function and pass the uid of the stopover:
 * ```js
 * hikingEditor.setStopOverDuration(123, 30);
 * ```
 *
 * Can also be used to reset the duration to the computed value by passing `undefined`:
 * ```js
 * hikingEditor.setStopOverDuration(123, undefined);
 * ```
 *
 * Calling this method will trigger a `change` event.
 *
 * ### Set distance to a stopover
 * To set a custom distance of a stopover in meters, use the {@link HikingEditor.setStopOverDistance} function and pass the uid of the stopover:
 * ```js
 * hikingEditor.setStopOverDistance(123, 1000);
 * ```
 *
 * Can also be used to reset the distance to the computed value by passing `undefined`:
 * ```js
 * hikingEditor.setStopOverDistance(123, undefined);
 * ```
 *
 * Calling this method will trigger a `change` event.
 *
 * ### Remove stopover
 * To remove a stopover, use the {@link HikingEditor.removeStopOver} function and pass the uid of the stopover:
 * ```js
 * hikingEditor.removeStopOver(123);
 * ```
 *
 * When a stopover is removed, the custom name and features are also removed and cannot be recovered.
 * Calling this method will trigger a `change` event.
 *
 * ### Reset custom facts
 * To reset the custom facts for the hiking and stopovers, use the {@link HikingEditor.resetCustomFacts} function:
 * ```js
 * hikingEditor.resetCustomFacts();
 * ```
 * This will recalculate the duration, distance, ascent, descent and physical_demand based only in the geometry.
 *
 * ## GPX and KML Import
 *
 * To create a **hiking from a GPX or a KML file**, use the {@link HikingEditor.importGpxOrKml} function and pass the file.
 * For GPX, the line coordinates will be primarily extracted from tracks and if no tracks are found, from routes.
 * Control points for both formats are snapped to the line - meaning that every control point
 * has a corresponding point on the line.
 * The first and last control points are always created as stopovers, even if no waypoints are found.
 * The coordinates are expected to be in EPSG:4326 projection and in Cartesian order (lon, lat).
 *
 * ## Multiline support
 *
 * To create a hiking with multiple lines, use the {@link HikingEditor.createNewLine} function.
 * This function will create a new line part and activate it for editing.
 * The maximum number of line parts is 10, if the limit is reached, the function will return false.
 *
 * A line can be deleted with the {@link HikingEditor.deleteLine} function.
 *
 */
const DISCIPLINE_TO_GRAPHHOPPER_PROFILE: Record<Discipline, string> = {
  hiking: "schmwander",
  "winter hiking": "schmwinterhiking",
  "snowshoe hiking": "schmsnowshoe",
};

// Profiles that use flexible mode
export const FLEXIBLE_GRAPHHOPPER_PROFILES = new Set<string>([
  "schmwinterhiking",
  "schmsnowshoe",
  "schmsledging",
  "schmskating",
  "schmcrosscountry",
  "schmcanoe",
]);

export default class HikingEditor extends EventTarget {
  private trackManager: TrackManager<unknown>;
  private readonly router: GraphHopperRouter;
  private readonly graphhopperBaseUrl: string;
  private stopOvers: Record<string, StopOver> = {};
  private onTrackChangeCallback: () => void;
  private hikingType!: HikingType;
  private uuid?: string;
  private hikingProperties: Record<string, unknown> = {};
  private _discipline: Discipline = "hiking";
  private trackSource: VectorSource;
  private readonly snapFeatures: Collection<Feature>;
  private view: View;
  // when true, we have added a manual history entry when switching from edit to view mode
  // we do not want this entry to be undone.
  private manualHistoryEntry = false;
  private stopOverHistoryManager = new HistoryManager<Record<string, StopOver>>();

  constructor(options: HikingEditorOptions) {
    super();

    this.setType(options.type);

    this.view = options.map.getView();

    this.trackSource = new VectorSource();

    const trackLayer = new VectorLayer({
      source: this.trackSource,
      style: trackStyle,
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    });
    // maintain our own ids because the track manager clones features on undo/redo
    this.trackSource.on("addfeature", (event: VectorSourceEvent) => {
      const feature = event.feature as Feature<Point>;
      if (feature.get("sww_id") === undefined) {
        feature.set("sww_id", crypto.randomUUID());
      }
      // FIXME: this is needed for the style where we have ["==", ["get", "stopover"], false]
      // instead we should use ["==", ["coalesce", ["get", "stopover"], false], false] but 'coalesce' in not
      // available in current ol version.
      if (feature.get("stopover") === undefined) {
        feature.set("stopover", false);
      }
      if (feature.get("zIndex") === undefined) {
        feature.set("zIndex", TRACK_Z_INDEX);
      }
    });

    const shadowTrackLayer = new VectorLayer({
      source: new VectorSource(),
      style: shadowTrackStyle,
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    });
    options.map.addLayer(shadowTrackLayer);
    options.map.addLayer(trackLayer);
    this.graphhopperBaseUrl = options.graphhopperUrl ?? "https://graphhopper-all.schweizmobil.ch";
    this.router = new GraphHopperRouter({
      map: options.map,
      url: this.createRouterUrl(this._discipline),
      maxRoutingTolerance: 15,
    });

    const densifier = new UnsnappedDensifier({
      distance: 10,
      maxPoints: 200,
    });
    const profiler = new FallbackProfiler({
      profilers: [
        new ExtractFromSegmentProfiler({ projection: this.view.getProjection() }),
        new SwisstopoProfiler({
          projection: this.view.getProjection(),
        }),
      ],
    });

    // FIXME: remove 'as VectorLayer<any>' when upgrading to OpenLayers 10
    this.trackManager = new TrackManager({
      map: options.map,
      router: this.router,
      densifier: densifier,
      profiler: profiler,
      trackLayer: trackLayer as VectorLayer<any>,
      shadowTrackLayer: shadowTrackLayer as VectorLayer<any>,
      style: trackStyle,
      deleteCondition: doubleClick,
      addLastPointCondition: singleClick, // we have to use single click otherwise the double click is not fired
      addControlPointCondition: doubleClick,
      hitTolerance: 15,
    });
    this.edit = true;

    this.onTrackChangeCallback = this.onTrackChange.bind(this);

    this.snapFeatures = new Collection();

    options.map.addInteraction(new Snap({ features: this.snapFeatures }));

    options.map.on("click", this.onMapClick.bind(this));
    this.trackManager.addTrackChangeEventListener(this.onTrackChangeCallback);

    this.loadHiking(options.uuid);
  }

  /**
   * Set the type of the hiking. The type can be one of `hiking`, `hiking_activity` or `hiking_proposition`.
   */
  public setType(type: HikingType) {
    if (!hikingTypes.includes(type)) {
      throw new Error(`invalid hiking type: '${type}'`);
    }
    this.hikingType = type;
  }

  /**
   * Returns the uuid of the hiking. If the hiking is not saved yet, this function returns undefined.
   */
  public getUuid(): string | undefined {
    return this.uuid;
  }

  private async loadHikingFeatures(hiking: ServerHiking): Promise<void> {
    const features = readFeatures(hiking, this.view.getProjection()) || [];
    await this.restoreFeatures(features);
  }

  private loadCustomProperties(hiking: ServerHiking): void {
    Object.assign(this.hikingProperties, {
      physical_demand: hiking.custom_physical_demand ? hiking.physical_demand : undefined,
      height_difference_upwards: hiking.custom_height_difference_upwards ? hiking.height_difference_upwards : undefined,
      height_difference_downwards: hiking.custom_height_difference_downwards
        ? hiking.height_difference_downwards
        : undefined,
    });
    // Load discipline from the hiking
    if (hiking.discipline) {
      this.discipline = hiking.discipline;
    }
  }

  /**
   * Load the hiking from the server.
   */
  public async loadHiking(uuid: string | undefined): Promise<void> {
    this.uuid = uuid;
    if (this.uuid) {
      this.edit = false;
      // deactivate the track change event listener, otherwise the listener
      // will convert the stopovers to control points.
      this.trackManager.removeTrackChangeEventListener(this.onTrackChangeCallback);

      this.clear();

      const hiking = await fetchHiking(this.uuid, this.hikingType);
      if (hiking.coordinates != null && hiking.control_points_with_stopovers != null) {
        await this.loadHikingFeatures(hiking);
        this.loadCustomProperties(hiking);
        this.dispatchCustomEvent("loaded");
        this.dispatchCustomEvent("change");
      } else {
        console.error("No geometry found for the hiking");
      }
      this.trackManager.addTrackChangeEventListener(this.onTrackChangeCallback);
    }
    this.edit = true;
  }

  /**
   * Create or update the hiking to the server
   */
  public async save(): Promise<string> {
    this.edit = false;
    const geometries = writeFeatures(
      this.trackManager.getAllSegments().filter((s) => s.length >= 1),
      this.trackManager.getAllControlPoints().filter((c) => c.length >= 2),
      this.stopOvers,
      this.view.getProjection(),
    );
    const properties = {
      ...this.hikingProperties,
      discipline: this.discipline,
    };
    if (this.uuid === undefined) {
      const { uuid, discipline } = await createHiking(this.hikingType, geometries, properties);
      this.uuid = uuid;
      this.discipline = discipline;
    } else {
      // TODO: this removes the values set with updateHikingProperties
      // TODO: check with Pedro why this change was made
      // const hiking = await fetchHiking(this.uuid, this.hikingType);
      // if (hiking.organisation) this.updateHikingProperties({ organisation: hiking.organisation });
      const { discipline } = await updateHiking(this.uuid, this.hikingType, geometries, properties);
      this.discipline = discipline;
    }
    this.edit = true;
    return this.uuid;
  }

  /**
   * Handle import of GPX and KML files
   */
  public importGpxOrKml(file: File): void {
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    const reader = new FileReader();

    reader.onload = () => {
      if (fileExtension === "kml") {
        const kmlFormat = new KML({ extractStyles: false });
        const featuresFromFile = kmlFormat.readFeatures(reader.result);
        this.loadHikingFromKml(featuresFromFile);
      } else if (fileExtension === "gpx") {
        const gpxString = reader.result as string;
        const [parsedGPX, error] = parseGPX(gpxString);
        if (error) throw error;
        this.loadHikingFromGpx(parsedGPX);
      } else throw new Error("Invalid file type. Please provide a .gpx or .kml file.");
    };

    reader.onerror = (event) => {
      throw new Error(`File could not be read: ${event.target?.error}`);
    };

    this.clear();
    reader.readAsText(file);
  }

  private async restoreFeatures(features: Feature<LineString | Point>[]) {
    await this.trackManager.restoreFeatures(features);

    for (const feature of features) {
      if (feature.get("stopover") === true) {
        this.addStopOverFromFeature(feature as Feature<Point>, true);
      }
    }

    this.view.fit(this.trackSource.getExtent(), {
      padding: [30, 30, 30, 30],
    });
  }

  /**
   * Convert the features from a KML file into a hiking object
   */
  private async loadHikingFromKml(featuresFromFile: Feature<Geometry>[]): Promise<void> {
    this.edit = false;
    const hikingFromKml: UnsavedHiking = {
      control_points_with_stopovers: [],
      coordinates: [],
      discipline: "hiking",
    };

    const coordinates: number[][] = getKmlCoordinates(featuresFromFile);
    hikingFromKml.coordinates = [coordinates];

    hikingFromKml.control_points_with_stopovers = getKmlControlPoints(featuresFromFile, coordinates);

    const features = readFeatures(hikingFromKml, this.view.getProjection());

    await this.restoreFeatures(features);

    this.dispatchCustomEvent("loaded");
    this.dispatchCustomEvent("change");
    this.edit = true;
  }

  /**
   * Convert the parsed GPX file into a hiking object
   */
  private async loadHikingFromGpx(parsedGPX: ParsedGPX) {
    this.edit = false;
    const hikingFromGPX: UnsavedHiking = {
      control_points_with_stopovers: [],
      coordinates: [],
      discipline: "hiking",
    };

    const coordinates: number[][] = getGpxCoordinates(parsedGPX);
    hikingFromGPX.coordinates = [coordinates];

    hikingFromGPX.control_points_with_stopovers = getGpxControlPoints(parsedGPX, coordinates);

    const features = readFeatures(hikingFromGPX, this.view.getProjection());

    await this.restoreFeatures(features);

    this.dispatchCustomEvent("loaded");
    this.dispatchCustomEvent("change");
    this.edit = true;
  }

  /**
   * Delete the hiking from the server
   */
  public delete() {
    throw new Error("not implemented");
  }

  /**
   * Update the hiking properties.
   * - Adds new properties that don't exist in hikingProperties
   * - Updates existing properties with new values
   * - Removes properties when their value is undefined
   */
  public updateHikingProperties(properties: Record<string, unknown>) {
    for (const [key, value] of Object.entries(properties)) {
      if (value === undefined) {
        delete this.hikingProperties[key];
      } else {
        this.hikingProperties[key] = value;
      }
    }

    // Special handling for discipline
    if ("discipline" in properties) {
      this.discipline = properties.discipline as Discipline;
    }

    this.dispatchCustomEvent("change");
  }

  public resetAllCustomFacts() {
    this.hikingProperties = {
      physical_demand: undefined,
      height_difference_upwards: undefined,
      height_difference_downwards: undefined,
    };
    for (const stopover of Object.values(this.stopOvers)) {
      stopover.properties.customDuration = undefined;
      stopover.properties.customDistance = undefined;
    }
    this.dispatchCustomEvent("change");
  }

  /**
   * Change the name of a stopover
   */
  public setStopOverName(uid: string, name: string) {
    const stopover = this.stopOvers[uid];
    if (stopover) {
      stopover.properties.customName = name;
      this.dispatchCustomEvent("change");
    }
  }

  /**
   * Set the features of a stopover
   */
  public setStopOverFeatures(uid: string, features: unknown[]) {
    // FIXME: validate features ?
    const stopover = this.stopOvers[uid];
    if (stopover) {
      stopover.properties.features = features;
      this.dispatchCustomEvent("change");
    }
  }

  /**
   * Set the duration of a stopover in minutes or reset it to the computed value (if `undefined` is passed)
   */
  public setStopOverDuration(uid: string, duration: number | string) {
    const stopover = this.stopOvers[uid];
    if (stopover) {
      const value = +duration;
      stopover.properties.customDuration = Number.isNaN(value) ? undefined : value;
      this.dispatchCustomEvent("change");
    }
  }

  /**
   * Set the distance of a stopover in meters or reset it to the computed value (if `undefined` is passed)
   */
  public setStopOverDistance(uid: string, distance: number | string) {
    const stopover = this.stopOvers[uid];
    if (stopover) {
      const value = +distance;
      stopover.properties.customDistance = Number.isNaN(value) ? undefined : value;
      this.dispatchCustomEvent("change");
    }
  }

  /**
   * Remove a stopover from the stopover list
   */
  public removeStopOver(uid: string) {
    const stopover = this.stopOvers[uid];
    if (stopover) {
      this.resetNextStopoverCustomProperties(stopover.index);
      delete this.stopOvers[uid];
      this.onTrackChange();
    }
  }

  /**
   * Undo the last action
   */
  public undo() {
    if (this.canUndo()) {
      this.stopOvers = { ...this.stopOverHistoryManager.undo() };
      this.trackManager.undo();
    }
  }

  /**
   * Redo the last action
   */
  public redo() {
    if (this.canRedo()) {
      this.stopOvers = { ...this.stopOverHistoryManager.redo() };
      this.trackManager.redo();
    }
  }

  /**
   * Reverse the track
   */
  public reverse() {
    // reset all custom durations
    for (const stopover of Object.values(this.stopOvers)) {
      stopover.properties.customDuration = undefined;
      stopover.properties.customDistance = undefined;
    }
    this.stopOverHistoryManager.add({ ...this.stopOvers });
    this.trackManager.reverse();
  }

  /**
   * Computes the properties (statistics) of a track, including total time, distance, ascent, descent,
   * physical demand, time and distance for each stopover.
   *
   * @returns {TrackProperties}
   */
  public computeTrackProperties(): TrackProperties {
    const allSegments = this.trackManager.getAllSegments();
    const segments = allSegments.flat();
    const durations = segments.map((segment: Feature<LineString>) =>
      walkingTime(segment.get("profile"), this.discipline),
    );
    const upDownHeights = segments.map((segment: Feature<LineString>) => walkingAscentDescent(segment.get("profile")));
    const distances: number[] = segments.map((segment: Feature<LineString>) => segment.get("profile").at(-1).at(3));

    const distance = distances.reduce((prev, curr) => prev + curr, 0);
    const ascent = upDownHeights.reduce((prev, curr) => prev + curr.ascent, 0);
    const descent = upDownHeights.reduce((prev, curr) => prev + curr.descent, 0);
    const time = durations.reduce((prev, curr) => prev + curr, 0);
    const physicalDemand = walkingPhysicalDemand(distance, ascent, descent, this.discipline);

    // stopover.index is a CP index; segment i connects CP i to CP i+1, so segment counts are used here.
    const segmentCounts = allSegments.map((s) => s.length);

    const stopovers: StopoverProperties[] = [];
    if (this.sortedStopovers().length > 0) {
      // first stopover is always 0
      stopovers.push({ time: 0, distance: 0, ascent: 0, descent: 0 });
      for (const [previous, current] of pairwise(this.sortedStopovers())) {
        const prevGlobalIndex = toGlobalIndex(segmentCounts, previous.part, previous.index);
        const currGlobalIndex = toGlobalIndex(segmentCounts, current.part, current.index);
        stopovers.push({
          time: durations.slice(prevGlobalIndex, currGlobalIndex).reduce((a: number, b: number) => a + b, 0),
          distance: distances.slice(prevGlobalIndex, currGlobalIndex).reduce((a: number, b: number) => a + b, 0),
          ascent: upDownHeights.slice(prevGlobalIndex, currGlobalIndex).reduce((a, b) => a + b.ascent, 0),
          descent: upDownHeights.slice(prevGlobalIndex, currGlobalIndex).reduce((a, b) => a + b.descent, 0),
        });
      }
    }
    return {
      totals: {
        time,
        distance,
        ascent,
        descent,
        physicalDemand,
      },
      stopovers,
    };
  }

  public get snapping(): boolean {
    return this.trackManager.snapping;
  }

  public set snapping(value: boolean) {
    this.trackManager.snapping = value;
  }

  public get edit(): boolean {
    return this.trackManager.mode === "edit";
  }

  public set edit(value: boolean) {
    this.trackManager.mode = value ? "edit" : "";
    if (!value) {
      this.trackManager.addManualHistoryEntry();
      this.manualHistoryEntry = true;
    }
    this.dispatchCustomEvent("change");
  }

  public get discipline(): Discipline {
    return this._discipline;
  }

  public set discipline(value: Discipline) {
    this._discipline = value;
    this.router.url = this.createRouterUrl(value);
    this.dispatchCustomEvent("change");
  }

  private createRouterUrl(discipline: Discipline): string {
    const profile = DISCIPLINE_TO_GRAPHHOPPER_PROFILE[discipline];
    const base = `${this.graphhopperBaseUrl}/route?profile=${profile}&type=json&elevation=true&way_point_max_distance=0&instructions=false&points_encoded=true&details=surface&details=structure&details=hiking_category`;
    const flexible = FLEXIBLE_GRAPHHOPPER_PROFILES.has(profile);
    return flexible ? `${base}&ch.disable=true` : base;
  }

  public getProfile(): HikingProfile {
    return getProfile(
      this.trackManager.getAllSegments() as Feature<LineString>[][],
      this.sortedStopovers().map((stopover) => [stopover.part, stopover.index]),
    );
  }

  public createNewLine(): boolean {
    if (this.trackManager.partsCount() >= 10) {
      return false;
    }
    this.trackManager.createNewPart();
    return true;
  }

  /**
   * Delete a line part by its index or the active part if no index is provided.
   */
  public deleteLine(partIndex: number) {
    if (Object.keys(this.stopOvers).length === 0) return;

    const index = partIndex ?? this.trackManager.activePart();

    this.trackManager.deletePart(index);

    // Update the part index for stopovers after the deleted part
    for (const key of Object.keys(this.stopOvers)) {
      if (this.stopOvers[key].part === index) {
        console.log("delete stopover");
        delete this.stopOvers[key];
      }

      if (this.stopOvers[key].part > index) {
        this.stopOvers[key].part -= 1;
      }
    }
  }

  private sortedStopovers(): StopOver[] {
    // sort by part and index
    return Object.values(this.stopOvers).sort((a, b) => a.part - b.part || a.index - b.index);
  }

  private async onMapClick(event: MapBrowserEvent<PointerEvent>) {
    if (!this.edit) {
      return;
    }
    const features = (event.map.getFeaturesAtPixel(event.pixel) as Feature<Point>[]).filter((feature) =>
      isControlPoint(feature),
    );

    if (features.length > 0) {
      const point = features[0];
      if (point.get("part") !== this.trackManager.activePart()) {
        return;
      }
      if (this.stopOvers[getStopOverId(point)] === undefined) {
        this.addStopOverFromFeature(point, true);
        this.stopOverHistoryManager.add({ ...this.stopOvers });
        this.dispatchCustomEvent("change");
      }
      event.originalEvent.preventDefault();
    }
  }

  private async dispatchCustomEvent(name: string) {
    const trackProperties = this.computeTrackProperties();

    const sortedStopovers = this.sortedStopovers();
    const stopovers = await Promise.all(
      trackProperties.stopovers.map(async (soProp, index) => {
        const stopover = sortedStopovers[index];
        if (stopover.properties.swissNames.length === 0) {
          stopover.properties.swissNames = await swissNames(stopover.coordinates, this.view.getProjection());
        }
        stopover.properties.duration = soProp.time;
        stopover.properties.distance = soProp.distance;
        stopover.properties.ascent = soProp.ascent;
        stopover.properties.descent = soProp.descent;
        return {
          uid: stopover.uid,
          swissNames: stopover.properties.swissNames,
          name: stopover.properties.customName ?? stopover.properties.swissNames[0],
          features: stopover.properties.features,
          duration: stopover.properties.customDuration ?? stopover.properties.duration,
          distance: stopover.properties.customDistance ?? stopover.properties.distance,
          ascent: stopover.properties.ascent,
          descent: stopover.properties.descent,
          coordinates: transform(stopover.coordinates, this.view.getProjection(), "EPSG:4326"),
        };
      }),
    );

    const detail = {
      type: this.hikingType,
      distance: trackProperties.totals.distance,
      time: trackProperties.totals.time,
      physical_demand: this.hikingProperties.physical_demand ?? trackProperties.totals.physicalDemand,
      ascent: this.hikingProperties.height_difference_upwards ?? trackProperties.totals.ascent,
      descent: this.hikingProperties.height_difference_downwards ?? trackProperties.totals.descent,
      canUndo: this.canUndo(),
      canRedo: this.canRedo(),
      stopovers: stopovers,
    };
    this.dispatchEvent(new CustomEvent(name, { detail: detail }));
    this.syncHistory();
  }

  private syncHistory() {
    const stopoversHistoryCount = this.stopOverHistoryManager.entryCount();
    const trackHistoryCount = this.trackManager.historyEntryCount;

    if (stopoversHistoryCount > trackHistoryCount) {
      this.trackManager.addManualHistoryEntry();
    } else if (stopoversHistoryCount < trackHistoryCount) {
      this.stopOverHistoryManager.add({ ...this.stopOvers });
    }
  }

  private clear() {
    this.stopOvers = {};
    this.hikingProperties = {};
    this.trackManager.clear();
  }

  private canUndo(): boolean {
    return this.trackManager.historyPosition >= (this.manualHistoryEntry ? 1 : 0);
  }

  private canRedo(): boolean {
    return this.trackManager.historyPosition < this.trackManager.historySize - 1;
  }

  private async onTrackChange() {
    const allPoints = this.trackManager.getAllControlPoints();
    // Remove deleted points from stopovers
    const uids = allPoints.flat().map(getStopOverId);
    for (const uid of Object.keys(this.stopOvers)) {
      if (!uids.includes(uid)) {
        this.resetNextStopoverCustomProperties(this.stopOvers[uid].index);
        delete this.stopOvers[uid];
      }
    }

    this.snapFeatures.clear();

    // first and last point of each part are snap points
    const snapPoints = allPoints.flatMap((points) => [points.at(0), points.at(-1)]) as Feature<Point>[];

    this.snapFeatures.extend(snapPoints);

    for (const points of allPoints) {
      let customPropertiesReset = false;
      for (const point of points) {
        const stopover = this.stopOvers[getStopOverId(point)];
        if (stopover) {
          // update existing stopovers
          const index = point.get("index");
          if (stopover.index !== index) {
            if (!customPropertiesReset) {
              // one control point has been added or removed in the segment before the stopover, reset custom duration and distance.
              stopover.properties.customDuration = undefined;
              stopover.properties.customDistance = undefined;
              // all the indexes after that will change but we don't want to reset the custom duration, that's the reason
              // for the customPropertiesReset variable.
              customPropertiesReset = true;
            }
            stopover.index = index;
          }
          const coordinates = point.getGeometry()?.getCoordinates() ?? [];
          // check if the point has moved, because of the routing in edittrack we can have
          // small difference but we ignore them
          if (distance(stopover.coordinates, coordinates) > 5) {
            stopover.coordinates = coordinates;
            // the point has moved, swiss names need to be recomputed
            stopover.properties.swissNames.length = 0;
            // reset custom duration and distance
            stopover.properties.customDuration = undefined;
            stopover.properties.customDistance = undefined;
            // reset custom duration of the next stopover
            this.resetNextStopoverCustomProperties(stopover.index);
          }
          point.set("stopover", true);
        } else {
          point.set("stopover", false);
        }
      }
      if (points.length >= 2) {
        const firstPoint = points.at(0) as Feature<Point>;
        const lastPoint = points.at(-1) as Feature<Point>;
        const firstStopOver = this.stopOvers[getStopOverId(firstPoint)];
        if (!firstStopOver) {
          this.addStopOverFromFeature(firstPoint, false);
        }
        // add last stopover if missing
        const lastStopOver = this.stopOvers[getStopOverId(lastPoint)];
        if (!lastStopOver) {
          this.addStopOverFromFeature(lastPoint, false);
          if (points.length > 2) {
            // remove previous last stopover but only if it was not set by the user
            this.removeStopOverFromFeature(points.at(-2) as Feature<Point>);
          }
        }
      }
    }

    // check if a part needs to be deleted because all its control points have been removed
    // don't delete the empty last part because deleteLine will recreate it and it will create an infinite loop.
    for (const tm of this.trackManager.partsGenerator()) {
      if (tm.getControlPoints().length > 0) {
        continue;
      }
      if (tm.activePart() === tm.partsCount() - 1) {
        continue;
      }
      this.deleteLine(tm.activePart());
    }

    this.computeStopOverLabels();
    this.dispatchCustomEvent("change");
  }

  private resetNextStopoverCustomProperties(index: number) {
    // FIXME
    const nextStopover = this.sortedStopovers().find((stopover) => stopover.index > index);
    if (nextStopover) {
      nextStopover.properties.customDuration = undefined;
      nextStopover.properties.customDistance = undefined;
    }
  }

  private addStopOverFromFeature(point: Feature<Point>, setByUser: boolean) {
    const uid = getStopOverId(point);

    this.stopOvers[uid] = {
      uid: uid,
      part: point.get("part"),
      // index among the control points of the part
      index: point.get("index"),
      coordinates: point.getGeometry()?.getCoordinates() ?? [],
      properties: {
        customName: point.get("sww_name"),
        features: point.get("sww_features") ?? [],
        swissNames: [],
        duration: point.get("sww_duration") ?? Number.NaN,
        customDuration: point.get("sww_custom_duration") ? (point.get("sww_duration") ?? undefined) : undefined,
        distance: point.get("sww_distance") ?? Number.NaN,
        customDistance: point.get("sww_custom_distance") ? (point.get("sww_distance") ?? undefined) : undefined,
        ascent: Number.NaN,
        descent: Number.NaN,
      },
    };
    point.set("stopover", true);
    point.set("stopover_set_by_user", setByUser);
    this.computeStopOverLabels();
  }

  private removeStopOverFromFeature(point: Feature<Point>) {
    if (point.get("stopover_set_by_user") === true) {
      return;
    }
    const uid = getStopOverId(point);
    delete this.stopOvers[uid];
    point.set("stopover", false);
    this.computeStopOverLabels();
  }

  private computeStopOverLabels() {
    const points = this.trackManager.getAllControlPoints().flat();
    const stopovers = this.sortedStopovers();
    for (const [index, stopover] of enumerate(stopovers)) {
      const point = points.find((p) => getStopOverId(p) === stopover.uid);
      if (!point) {
        throw new Error("stopover not found");
      }
      point.set("label", indexToLabel(index));
      point.set("zIndex", TRACK_Z_INDEX + stopovers.length - index);
    }
    // remove labels from the first and last point
    points.at(0)?.set("label", "");
    points.at(-1)?.set("label", "");
  }
}

export function getStopOverId(point: Feature<Point>): string {
  return point.get("sww_id");
}

function isControlPoint(point: Feature<Point>) {
  return point.get("type") === "controlPoint";
}
